import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { EmptyState } from '@/components/EmptyState';
import { GettingStarted } from '@/components/GettingStarted';
import { HelpButton } from '@/components/HelpButton';
import { fetchDashboard, fmtBRL } from '@/lib/finance';

export function DashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboard,
  });

  if (isLoading) return <p className="text-slate-500">carregando…</p>;
  if (error) return <p className="text-red-600">falha: {(error as Error).message}</p>;
  if (!data) return null;

  const hasAccounts = data.accounts.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center gap-2">
        <h1 className="text-xl font-semibold">Visão geral</h1>
        <HelpButton topic="dashboard" />
      </header>

      <GettingStarted />

      {!hasAccounts && (
        <EmptyState
          title="Você ainda não tem contas cadastradas"
          hint="Comece adicionando uma conta corrente, poupança ou carteira física pra ter saldos calculados."
          icon="🏦"
          action={
            <Link
              to="/accounts"
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              Criar primeira conta
            </Link>
          }
        />
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card label="saldo total" value={fmtBRL(data.accounts_total_balance)} />
        <Card label="receitas (mês)" value={fmtBRL(data.month_income)} positive />
        <Card label="despesas (mês)" value={fmtBRL(data.month_expense)} negative />
        <Card
          label="resultado (mês)"
          value={fmtBRL(data.month_net)}
          positive={parseFloat(data.month_net) >= 0}
          negative={parseFloat(data.month_net) < 0}
        />
      </div>

      {hasAccounts && (
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-slate-500">
            contas
          </h2>
          <ul className="divide-y divide-slate-100 text-sm">
            {data.accounts.map((a) => (
              <li key={a.id} className="flex items-center justify-between py-2">
                <span className="truncate">
                  {a.name} <span className="text-slate-400">· {a.type}</span>
                </span>
                <span
                  className={`shrink-0 ${
                    parseFloat(a.balance) < 0 ? 'text-red-600' : 'text-slate-800'
                  }`}
                >
                  {fmtBRL(a.balance)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-slate-500">
          top despesas do mês
        </h2>
        {data.top_categories_expense.length === 0 ? (
          <p className="text-sm text-slate-500">sem dados ainda neste mês.</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {data.top_categories_expense.map((c) => (
              <li
                key={c.category_id ?? c.category_name}
                className="flex items-center justify-between py-2"
              >
                <span className="truncate">
                  {c.category_name}{' '}
                  <span className="text-slate-400">({c.count})</span>
                </span>
                <span className="shrink-0 text-red-600">{fmtBRL(c.total)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-slate-500">
          últimos meses
        </h2>
        {data.monthly_series.length === 0 ? (
          <p className="text-sm text-slate-500">sem histórico.</p>
        ) : (
          <div className="-mx-4 overflow-x-auto px-4">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-2">mês</th>
                  <th className="py-2 text-right">receitas</th>
                  <th className="py-2 text-right">despesas</th>
                  <th className="py-2 text-right">líquido</th>
                </tr>
              </thead>
              <tbody>
                {data.monthly_series.map((p) => {
                  const net = parseFloat(p.income) - parseFloat(p.expense);
                  return (
                    <tr key={p.month} className="border-t border-slate-100">
                      <td className="py-2 font-mono">{p.month}</td>
                      <td className="py-2 text-right text-emerald-700">
                        {fmtBRL(p.income)}
                      </td>
                      <td className="py-2 text-right text-red-600">
                        {fmtBRL(p.expense)}
                      </td>
                      <td
                        className={`py-2 text-right ${
                          net < 0 ? 'text-red-600' : 'text-slate-800'
                        }`}
                      >
                        {fmtBRL(net)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Card({
  label,
  value,
  positive,
  negative,
}: {
  label: string;
  value: string;
  positive?: boolean;
  negative?: boolean;
}) {
  const color = positive ? 'text-emerald-700' : negative ? 'text-red-600' : 'text-slate-900';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm lg:p-4">
      <p className="text-[11px] uppercase tracking-wide text-slate-500 lg:text-xs">{label}</p>
      <p className={`mt-1 text-base font-semibold lg:text-xl ${color}`}>{value}</p>
    </div>
  );
}
